'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { 
  Filter,
  ChevronDown,
  ExternalLink,
  Loader2,
  Package,
  SlidersHorizontal,
  Calendar,
  Sun,
  Snowflake,
  Leaf,
  Flower
} from 'lucide-react';
import { Button } from '@myapp/ui/components/button';
import { Card, CardContent } from '@myapp/ui/components/card';
import { Badge } from '@myapp/ui/components/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@myapp/ui/components/select';
import { ToggleGroup, ToggleGroupItem } from '@myapp/ui/components/toggle-group';
import { Skeleton } from '@myapp/ui/components/skeleton';
import { ProductAnalysis } from '@/components/ProductAnalysis';

// 강원도 지역별 특산물 (월별 분류)
const productsByMonth = {
  1: [ // 1월 - 겨울
    '인제 황태', '양양 황태', '평창 황태', '고성 명태', '고성 도로묵', '고성 뚝지',
    '고성 대게', '고성 털게', '강릉 대게', '양양 대게', '삼척 대게',
    '속초 아바이순대', '속초 오징어순대', '원주 순대',
    '횡성한우', '정선 한우', '강릉 한우', '대관령 한우', '치악산 한우',
    '철원 오대쌀', '홍천 쌀', '영월 쌀', '인제 토토미', '원주 토토미',
    '양구 시래기', '홍천 건나물', '정선 콩', '정선 팥'
  ],
  2: [ // 2월 - 겨울
    '춘천 닭갈비', '춘천 막국수', '삼척 물닭갈비', '속초 닭강정',
    '강릉 감자옹심이', '강릉 초당순두부', '인제 순두부',
    '정선 막국수', '정선 올챙이국수', '정선 콧등치기국수', '강릉 올챙이국수', '인제 칡국수',
    '평창 메밀', '정선 메밀', '철원 메밀',
    '홍천 잣', '인제 잣', '정선 잣', '양구 호두'
  ],
  3: [ // 3월 - 봄 시작
    '인제 고로쇠수액', '인제 참두릅', '정선 참두릅', '정선 개두릅', 
    '강릉 두릅', '정선 곤드레', '강릉 곤드레',
    '인제 명이나물', '영월 산마늘', '정선 취나물', '정선 어수리',
    '태백 산죽차', '춘천 총떡', '삼척 딸기'
  ],
  4: [ // 4월 - 봄
    '강릉 곰취', '동해 곰취', '양구 곰취', '인제 곰취', '영월 곰취',
    '태백 곰취찐빵', '태백 곰취 산채냉면', '태백 산채 송편',
    '홍천 더덕', '영월 더덕', '횡성 더덕', '정선 도라지', '홍천 도라지',
    '인제 도라지청', '영월 더덕술',
    '삼척 원덕왕마늘', '양양 산채류'
  ],
  5: [ // 5월 - 늦봄
    '양구 아스파라거스', '인제 아스파라거스', '화천 가시오가피', '삼척 가시오가피',
    '강릉 옥수수', '홍천 찰옥수수', '정선 찰옥수수', '영월 찰옥수수', '평창 옥수수',
    '인제 미백찰옥수수', '정선 찰옥수수빵',
    '홍천 인삼', '철원 인삼', '영월 장뇌삼', '정선 산양삼', '인제 산양삼', '홍천 산양삼'
  ],
  6: [ // 6월 - 초여름
    '철원 토마토', '화천 토마토', '횡성 토마토', '영월 토마토',
    '양구 멜론', '평창 멜론', '춘천 블루베리', '인제 블루베리',
    '화천 산천어', '강릉 무지개송어', '평창 송어', '양양 연어',
    '춘천 감자빵', '강릉 짬뽕빵', '강릉 강릉샌드', '태백 곰취찐빵'
  ],
  7: [ // 7월 - 한여름
    '평창 배추', '태백 배추', '홍천 배추', '강릉 배추', '정선 배추', '인제 배추',
    '횡성 절임배추', '영월 절임배추',
    '평창 감자', '강릉 감자', '인제 감자', '양구 감자', '정선 감자', '영월 감자',
    '화천 감자', '철원 감자', '속초 감자', '인제 수미감자',
    '원주 조엄고구마', '화천 버섯', '홍천 약용버섯', '홍천 상황버섯'
  ],
  8: [ // 8월 - 늦여름
    '치악산 복숭아', '원주 복숭아', '강릉 포도', '영월 포도', '원주 포도', '인제 포도',
    '영월 고추', '영월 고추가루', '영월 고추장', '춘천 고추장',
    '동해 해산물', '속초 해산물', '속초 생선', '고성 오징어', '속초 오징어',
    '양양 오징어', '양양 삼치', '양양 고등어'
  ],
  9: [ // 9월 - 초가을
    '치악산 사과', '동해 사과', '양구 사과', '횡성 사과', '평창 사과', '영월 사과',
    '원주 치악산 사과', '치악산 배', '원주 치악산 배',
    '양양 송이버섯', '인제 송이버섯', '홍천 표고버섯', '영월 표고버섯', '정선 표고버섯',
    '정선 상황버섯', '영월 느타리버섯', '원주 큰송이 버섯', '화천 노루궁뎅이버섯'
  ],
  10: [ // 10월 - 가을
    '홍천 오미자', '영월 오미자', '정선 오미자', '인제 오미자',
    '강릉 곶감', '영월 곶감', '정선 토종다래', '원주 치악산토종다래', '영월 다래',
    '철원 흑마늘', '인제 흑초', '춘천 흑초', '철원 막걸리', '인제 포도주',
    '동해 벌꿀', '양구 꿀', '화천 잣', '정선 들깨'
  ],
  11: [ // 11월 - 늦가을
    '정선 수수', '횡성 잡곡', '철원 삼지구엽초', '정선 능계승마',
    '양구 흰민들레즙', '홍천 장류', '춘천 된장', '인제 서리태',
    '횡성 안흥찐빵', '횡성 어사진미', '정선 곤드레밥', '정선 메밀전병',
    '태백 고원배추', '화천 붕어', '화천 메기'
  ],
  12: [ // 12월 - 겨울
    '양양 대왕문어', '고성 양미리', '고성 갯장어', '고성 왕새우', '고성 홍가리비',
    '동해 새우', '속초 왕새우', '고성 마른멸치', '동해 건어물', '동해 쥐포',
    '속초 젓갈류', '삼척 삼베', '삼척 곰치국', '동해 곰치국',
    '철원 쿨포크', '강릉 커피'
  ]
};

// 계절별 분류
const seasonalProductsData = {
  spring: [ // 봄 (3-5월)
    ...productsByMonth[3], ...productsByMonth[4], ...productsByMonth[5]
  ].filter((item, index, arr) => arr.indexOf(item) === index),
  
  summer: [ // 여름 (6-8월)
    ...productsByMonth[6], ...productsByMonth[7], ...productsByMonth[8]
  ].filter((item, index, arr) => arr.indexOf(item) === index),
  
  autumn: [ // 가을 (9-11월)
    ...productsByMonth[9], ...productsByMonth[10], ...productsByMonth[11]
  ].filter((item, index, arr) => arr.indexOf(item) === index),
  
  winter: [ // 겨울 (12-2월)
    ...productsByMonth[12], ...productsByMonth[1], ...productsByMonth[2]
  ].filter((item, index, arr) => arr.indexOf(item) === index)
};

// 모든 특산물 리스트
const allProducts = [
  ...Object.values(productsByMonth).flat()
].filter((item, index, arr) => arr.indexOf(item) === index);

// 계절 감지 함수
const getCurrentSeason = () => {
  const month = new Date().getMonth() + 1; // 0-11을 1-12로 변환
  
  if (month >= 3 && month <= 5) return 'spring';
  if (month >= 6 && month <= 8) return 'summer';
  if (month >= 9 && month <= 11) return 'autumn';
  return 'winter';
};

// 필터에 따른 상품 목록 가져오기
const getFilteredProductList = (selectedMonth: string, selectedSeason: string) => {
  if (selectedMonth !== 'all') {
    return productsByMonth[parseInt(selectedMonth) as keyof typeof productsByMonth] || [];
  }
  
  if (selectedSeason) {
    return seasonalProductsData[selectedSeason as keyof typeof seasonalProductsData] || [];
  }
  
  // 전체 선택 시 현재 계절 중심으로 표시
  const currentSeason = getCurrentSeason();
  const currentSeasonProducts = seasonalProductsData[currentSeason];
  const otherProducts = allProducts.filter(p => !currentSeasonProducts.includes(p));
  
  // 현재 계절 70% + 다른 계절 30%
  const currentWeight = Math.ceil(currentSeasonProducts.length * 0.7);
  const otherWeight = Math.ceil(otherProducts.length * 0.3);
  
  return [
    ...currentSeasonProducts.slice(0, currentWeight),
    ...otherProducts.slice(0, otherWeight)
  ];
};

const months = [
  { value: '1', label: '1월' },
  { value: '2', label: '2월' },
  { value: '3', label: '3월' },
  { value: '4', label: '4월' },
  { value: '5', label: '5월' },
  { value: '6', label: '6월' },
  { value: '7', label: '7월' },
  { value: '8', label: '8월' },
  { value: '9', label: '9월' },
  { value: '10', label: '10월' },
  { value: '11', label: '11월' },
  { value: '12', label: '12월' },
];

const seasons = [
  { value: 'spring', label: '봄', icon: Flower },
  { value: 'summer', label: '여름', icon: Sun },
  { value: 'autumn', label: '가을', icon: Leaf },
  { value: 'winter', label: '겨울', icon: Snowflake },
];

const sortOptions = [
  { value: 'popular', label: '인기순' },
  { value: 'price-low', label: '가격 낮은순' },
  { value: 'price-high', label: '가격 높은순' },
  { value: 'newest', label: '최신순' },
];

export default function SeasonalPage() {
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedSeason, setSelectedSeason] = useState('');
  const [selectedSort, setSelectedSort] = useState('popular');
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);

  // 실제 상품 데이터 로딩
  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      try {
        const searchQueries = getFilteredProductList(selectedMonth, selectedSeason);
        const allProducts = [];
        
        // 각 검색어로 상품 검색 (처음 30개만)
        for (let i = 0; i < Math.min(30, searchQueries.length); i++) {
          const query = searchQueries[i];
          try {
            const response = await fetch(`/api/search-products?query=${encodeURIComponent(query)}`);
            if (response.ok) {
              const data = await response.json();
              if (data.products && data.products.length > 0) {
                // 첫 번째 상품만 가져와서 중복 방지
                allProducts.push({
                  ...data.products[0],
                  originalQuery: query // 원본 검색어 저장
                });
              }
            }
          } catch (error) {
            console.error(`Error searching for ${query}:`, error);
          }
          
          // API 호출 간격 조정
          if (i % 5 === 0 && i > 0) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
        
        setProducts(allProducts);
        setHasMoreProducts(searchQueries.length > 30);
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [selectedMonth, selectedSeason]); // 필터 변경 시 다시 로드

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      const searchQueries = getFilteredProductList(selectedMonth, selectedSeason);
      const additionalProducts = [];
      const startIndex = products.length;
      
      // 추가 상품 로딩
      for (let i = startIndex; i < Math.min(startIndex + 30, searchQueries.length); i++) {
        const query = searchQueries[i];
        try {
          const response = await fetch(`/api/search-products?query=${encodeURIComponent(query)}`);
          if (response.ok) {
            const data = await response.json();
            if (data.products && data.products.length > 0) {
              additionalProducts.push({
                ...data.products[0],
                originalQuery: query
              });
            }
          }
        } catch (error) {
          console.error(`Error searching for ${query}:`, error);
        }
        
        if (i % 5 === 0) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      setProducts(prev => [...prev, ...additionalProducts]);
      setHasMoreProducts(startIndex + 30 < searchQueries.length);
    } catch (error) {
      console.error('Error loading more products:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // 정렬된 상품들
  const sortedProducts = [...products].sort((a, b) => {
    if (selectedSort === 'price-low') {
      return (a.rawPrice || 0) - (b.rawPrice || 0);
    }
    if (selectedSort === 'price-high') {
      return (b.rawPrice || 0) - (a.rawPrice || 0);
    }
    // 기본값: 인기순 (ID 순서)
    return a.id - b.id;
  });

  const getFilterDescription = () => {
    if (selectedMonth !== 'all') {
      return `${selectedMonth}월 특산물`;
    }
    if (selectedSeason) {
      const seasonLabel = seasons.find(s => s.value === selectedSeason)?.label;
      return `${seasonLabel} 특산물`;
    }
    const currentSeasonLabel = seasons.find(s => s.value === getCurrentSeason())?.label;
    return `현재 ${currentSeasonLabel} 제철 특산물 중심`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Filter Bar */}
      <div 
        className="sticky top-16 z-30 bg-white border-b border-gray-200 shadow-sm"
        data-onboarding="filter"
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Month Filter */}
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <Select value={selectedMonth} onValueChange={(value) => {
                setSelectedMonth(value);
                if (value !== 'all') {
                  setSelectedSeason(''); // 월 선택 시 계절 필터 해제
                }
              }}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="월 선택" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="all">전체</SelectItem>
                  {months.map((month) => (
                    <SelectItem key={month.value} value={month.value}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Season Toggle */}
            <div className="flex items-center gap-2">
              <ToggleGroup type="single" value={selectedSeason} onValueChange={(value) => {
                setSelectedSeason(value || '');
                if (value) {
                  setSelectedMonth('all'); // 계절 선택 시 월 필터 해제
                }
              }}>
                {seasons.map((season) => {
                  const Icon = season.icon;
                  return (
                    <ToggleGroupItem key={season.value} value={season.value} className="px-3">
                      <Icon className="w-4 h-4 mr-1" />
                      {season.label}
                    </ToggleGroupItem>
                  );
                })}
              </ToggleGroup>
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center gap-2 ml-auto">
              <SlidersHorizontal className="w-4 h-4 text-gray-500" />
              <Select value={selectedSort} onValueChange={setSelectedSort}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sortOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">제철 특산물</h1>
          <p className="text-gray-600">강원도의 신선한 제철 특산물을 만나보세요</p>
          <p className="text-sm text-green-600 mt-1">{getFilterDescription()}</p>
        </div>

        {/* Loading Skeleton */}
        {loading && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {[...Array(12)].map((_, index) => (
              <Card key={index} className="overflow-hidden">
                <CardContent className="p-0">
                  <Skeleton className="aspect-square" />
                  <div className="p-4 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                    <div className="flex gap-1">
                      <Skeleton className="h-5 w-12" />
                      <Skeleton className="h-5 w-16" />
                    </div>
                    <Skeleton className="h-6 w-24" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Product Grid */}
        {!loading && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {sortedProducts.map((product, index) => (
              <div key={`${product.id}-${index}`}>
                <Link href={product.link || `/products/product-${product.id}`} target={product.link ? "_blank" : "_self"}>
                  <Card 
                    className="group hover:shadow-lg transition-all cursor-pointer overflow-hidden"
                    data-onboarding={index === 0 ? "bookmark" : undefined}
                  >
                    <CardContent className="p-0">
                      {/* Product Image */}
                      <div className="aspect-square bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center text-7xl relative overflow-hidden">
                        {product.image}
                        <button 
                          className="absolute top-2 right-2 p-2 bg-white/90 backdrop-blur rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="w-4 h-4 text-gray-700" />
                        </button>
                      </div>
                      
                      {/* Product Info */}
                      <div className="p-4">
                        <h3 className="font-semibold text-gray-900 mb-1 truncate" title={product.name}>
                          {product.name}
                        </h3>
                        <p className="text-xs text-gray-500 mb-2">{product.unit}</p>
                        
                        {/* Tags */}
                        <div className="flex flex-wrap gap-1 mb-3">
                          {product.badge && (
                            <Badge variant="secondary" className="text-xs">
                              {product.badge}
                            </Badge>
                          )}
                          {product.discount && (
                            <Badge variant="destructive" className="text-xs">
                              {product.discount}
                            </Badge>
                          )}
                          {product.originalQuery && (
                            <Badge variant="outline" className="text-xs">
                              {product.originalQuery.split(' ')[0]} 특산물
                            </Badge>
                          )}
                        </div>
                        
                        {/* Price */}
                        <p className="text-lg font-bold text-green-600">
                          {product.price}
                        </p>
                        <p className="text-xs text-gray-500">
                          {product.mall}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                <ProductAnalysis 
                  key={`${product.id}-${index}-${selectedSort}`} 
                  product={product} 
                  size="sm" 
                />
              </div>
            ))}
          </div>
        )}


        {/* Load More Section */}
        {!loading && sortedProducts.length > 0 && hasMoreProducts && (
          <div className="mt-12 space-y-6">
            {/* Load More Button */}
            <div className="text-center">
              <Button
                onClick={handleLoadMore}
                variant="outline"
                size="lg"
                className="min-w-[200px]"
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    불러오는 중...
                  </>
                ) : (
                  <>
                    더 보기
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>

            {/* Skeleton Loading */}
            {loadingMore && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {[...Array(8)].map((_, index) => (
                  <Card key={index} className="overflow-hidden">
                    <CardContent className="p-0">
                      <Skeleton className="aspect-square" />
                      <div className="p-4 space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                        <div className="flex gap-1">
                          <Skeleton className="h-5 w-12" />
                          <Skeleton className="h-5 w-16" />
                        </div>
                        <Skeleton className="h-6 w-24" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!loading && sortedProducts.length === 0 && (
          <div className="text-center py-20">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">상품이 없습니다</h3>
            <p className="text-gray-500">선택하신 조건에 맞는 상품이 없습니다.<br />다른 필터를 선택해보세요.</p>
          </div>
        )}
      </div>
    </div>
  );
}